import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import type { Template } from "@/types/template";
import {
  type PaginationResult,
  type GraphQLConnection,
  pageToCursor,
  buildGraphQLSortVariable as buildSort,
  computeMockPagination,
} from "@/lib/graphql-pagination";

export type { PaginationResult } from "@/lib/graphql-pagination";

// --- Params ---

export interface GetTemplatesGraphQLParams {
  page?: number;
  pageSize?: number;
  search?: string;
  templateType?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

// --- GraphQL types ---

interface TemplateListGraphQLNode {
  templateId: number;
  name: string | null;
  templateTypeName: string | null;
  templateCategoryTypeName: string | null;
  subject: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string | null;
}

interface TemplateListGraphQLResponse {
  templates: {
    templateList: GraphQLConnection<TemplateListGraphQLNode>;
  };
}

// --- GraphQL query ---

const TEMPLATE_LIST_QUERY = `
  query TemplateList($first: Int, $after: String, $sort: [TemplateListSortSortByInput!], $filter: TemplateListFilterInput) {
    templates {
      templateList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            templateId
            name
            templateTypeName
            templateCategoryTypeName
            subject
            isDefault
            createdAt
            updatedAt
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

// --- Mapping ---

function mapGraphQLNodeToTemplate(node: TemplateListGraphQLNode): Template {
  const typeName = (node.templateTypeName ?? "").toLowerCase();
  const type: Template["type"] = typeName === "statement" ? "statement" : "email";

  return {
    id: String(node.templateId),
    name: node.name ?? "",
    type,
    subject: node.subject ?? undefined,
    content: "",
    isDefault: node.isDefault,
    category: node.templateCategoryTypeName ? [node.templateCategoryTypeName] : [],
    categoryTypeName: node.templateCategoryTypeName ?? undefined,
    variables: [],
    lastModified: node.updatedAt ?? node.createdAt,
    createdAt: node.createdAt,
    createdBy: "",
  };
}

// --- Sort / Filter builders ---

const sortFieldMap: Record<string, string> = {
  Name: "Name",
  UpdatedAt: "UpdatedAt",
  CreatedAt: "CreatedAt",
  TemplateTypeName: "TemplateTypeName",
};

function buildGraphQLSortVariable(sortBy?: string, sortOrder?: "asc" | "desc") {
  return buildSort(sortFieldMap, sortBy, sortOrder);
}

function buildGraphQLFilterVariable(params: GetTemplatesGraphQLParams) {
  const filter: Record<string, unknown> = {};

  if (params.search) {
    filter.summary = params.search;
  }
  if (params.templateType && params.templateType !== "all") {
    const typeMap: Record<string, string> = {
      email: "Email",
      statement: "Statement",
    };
    const typeName = typeMap[params.templateType];
    if (typeName) {
      filter.templateTypeName = { eq: typeName };
    }
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Mock data ---

const mockTemplates: Template[] = [
  {
    id: "1",
    name: "Payment Reminder",
    type: "email",
    subject: "Payment Reminder - Account <<accountNumber>>",
    content: "",
    isDefault: true,
    category: ["payment", "reminder"],
    variables: [],
    lastModified: "2024-01-15T10:00:00Z",
    createdAt: "2024-01-01T10:00:00Z",
    createdBy: "Admin User",
  },
  {
    id: "2",
    name: "Welcome Email",
    type: "email",
    subject: "Welcome to <<companyName>>",
    content: "",
    isDefault: false,
    category: ["welcome", "onboarding"],
    variables: [],
    lastModified: "2024-01-20T14:30:00Z",
    createdAt: "2024-01-10T09:00:00Z",
    createdBy: "Admin User",
  },
  {
    id: "3",
    name: "Monthly Statement",
    type: "statement",
    content: "",
    isDefault: true,
    category: ["monthly", "statement"],
    variables: [],
    lastModified: "2024-01-18T11:00:00Z",
    createdAt: "2024-01-05T10:00:00Z",
    createdBy: "Admin User",
  },
  {
    id: "4",
    name: "Overdue Notice",
    type: "email",
    subject: "Overdue Payment Notice",
    content: "",
    isDefault: false,
    category: ["overdue", "collections"],
    variables: [],
    lastModified: "2024-01-25T09:30:00Z",
    createdAt: "2024-01-12T10:00:00Z",
    createdBy: "Admin User",
  },
  {
    id: "6",
    name: "Payment Confirmation",
    type: "email",
    subject: "Payment Received - Thank You",
    content: "",
    isDefault: false,
    category: ["payment", "confirmation"],
    variables: [],
    lastModified: "2024-01-28T13:00:00Z",
    createdAt: "2024-01-15T10:00:00Z",
    createdBy: "Admin User",
  },
  {
    id: "7",
    name: "Final Notice",
    type: "email",
    subject: "FINAL NOTICE - Immediate Action Required",
    content: "",
    isDefault: false,
    category: ["final", "collections", "urgent"],
    variables: [],
    lastModified: "2024-01-30T16:00:00Z",
    createdAt: "2024-01-30T16:00:00Z",
    createdBy: "Admin User",
  },
];

function filterMockTemplates(templates: Template[], params: GetTemplatesGraphQLParams): Template[] {
  return templates.filter((template) => {
    const matchesSearch = !params.search ||
      template.name.toLowerCase().includes(params.search.toLowerCase()) ||
      (template.subject?.toLowerCase().includes(params.search.toLowerCase()) ?? false);

    const matchesType = !params.templateType || params.templateType === "all" || template.type === params.templateType;

    return matchesSearch && matchesType;
  });
}

function sortMockTemplates(templates: Template[], sortBy?: string, sortOrder?: "asc" | "desc"): Template[] {
  const sorted = [...templates];
  const order = sortOrder === "desc" ? -1 : 1;

  sorted.sort((a, b) => {
    if (sortBy === "Name") {
      return order * a.name.localeCompare(b.name);
    }
    if (sortBy === "UpdatedAt") {
      return order * (new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime());
    }
    return 0;
  });

  return sorted;
}

// --- Result type ---

export interface TemplatesListResult {
  templates: Template[];
  pagination: PaginationResult;
}

// --- Hook ---

export function useTemplatesListGraphQL(params?: GetTemplatesGraphQLParams) {
  const apiConfig = useApiConfig();
  const queryParams: GetTemplatesGraphQLParams = { page: 1, pageSize: 20, sortBy: "Name", sortOrder: "asc", ...params };
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 20;
  const filteredMock = filterMockTemplates(mockTemplates, queryParams);
  const sortedMock = sortMockTemplates(filteredMock, queryParams.sortBy, queryParams.sortOrder);
  const mockPagination = computeMockPagination(sortedMock.length, mockPage, mockPageSize);
  const mockPagedTemplates = sortedMock.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockResult: TemplatesListResult = { templates: mockPagedTemplates, pagination: mockPagination };

  return useModeAwareQuery<TemplatesListResult, Error>(
    ["templates-graphql-list", queryParams],
    async ({ signal }) => {
      const page = queryParams.page ?? 1;
      const pageSize = queryParams.pageSize ?? 20;

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter: buildGraphQLFilterVariable(queryParams),
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<TemplateListGraphQLResponse>(
        apiConfig,
        TEMPLATE_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.templates.templateList;
      const templates = connection.edges.map((x) => mapGraphQLNodeToTemplate(x.node));

      const pagination: PaginationResult = {
        page,
        pageSize,
        totalItems: templates.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { templates, pagination };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: "always",
    }
  );
}
